import { DateTimeInput, FormControl, FormField, FormItem, FormLabel, FormMessage, Helptext } from "@/shared/components";
import { extractTimeAndDate } from "@/shared/utils";
import { formatISO } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { ChangeEvent, useState } from "react";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { DateInput, TimeInput } from "./input-datetime";

interface FormFieldInputDateTimeProps<T extends FieldValues> {
    form: UseFormReturn<T>;
    label?: string;
    name: FieldPath<T>;
    defaultTime?: string;
    defaultDate?: Date;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    readOnly?: boolean;
    helptext?: string;
}

/* A combined field that shows time + date inputfield separately, with one label and one error message. */
export function FormFieldInputDateTime<T extends FieldValues>({
    form,
    label,
    name,
    defaultTime,
    defaultDate,
    className,
    labelClassName,
    readOnly,
    helptext,
}: Readonly<FormFieldInputDateTimeProps<T>>) {
    const value = form.getValues(name);
    const [timeValue, dateValue] = extractTimeAndDate(value);
    const [time, setTime] = useState<string | undefined>(timeValue ?? defaultTime);
    const [date, setDate] = useState<Date | undefined>(dateValue ?? defaultDate);

    const combineTimeAndDate = (newTime: string | undefined, newDate: Date | undefined, onChangeHandler: (date: Date | null) => void) => {
        if (!newTime || !newDate) {
            // if either newTime or newDate is empty / falsy, reset form value and trigger validation
            onChangeHandler(null);
            void form.trigger(name);
            return;
        }

        // combine time and date to a zoned datetime (in Europe/Amsterdam) and call onChange
        const timeString = `${newTime}:00`;
        const dateString = formatISO(newDate, { representation: "date" });
        const combinedString = `${dateString}T${timeString}`;
        const combinedDate = fromZonedTime(combinedString, "Europe/Amsterdam");
        onChangeHandler(combinedDate);
    };

    const onDateChange = (onChangeHandler: (date: Date | null) => void) => (date: Date) => {
        setDate(date);
        combineTimeAndDate(time, date, onChangeHandler);
    };

    const onTimeChange = (onChangeHandler: (date: Date | null) => void) => (e: ChangeEvent<HTMLInputElement, HTMLInputElement>) => {
        const time = e.target.value;
        setTime(time);
        combineTimeAndDate(time, date, onChangeHandler);
    };

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem className={className}>
                    {label && <FormLabel className={labelClassName}>{label}</FormLabel>}
                    <FormControl>
                        <DateTimeInput>
                            <TimeInput readOnly={readOnly} value={time} onChange={onTimeChange(field.onChange)} />
                            <DateInput readOnly={readOnly} date={date} onSelect={onDateChange(field.onChange)} />
                        </DateTimeInput>
                    </FormControl>
                    <div>
                        {helptext && <Helptext>{helptext}</Helptext>}
                        <FormMessage />
                    </div>
                </FormItem>
            )}
        />
    );
}
